import { Gamepad2 } from "lucide-react";
import { FaSteam, FaDiscord, FaVk } from "react-icons/fa";
import { useState } from "react";
import MatrixRain from "@/components/MatrixRain";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import GameProgress from "@/components/GameProgress";
import GameShowcase from "@/components/GameShowcase";
import FloatingParticles from "@/components/FloatingParticles";
import { games } from "@/data/gamedev-data";

const Games = () => {
  const [filter, setFilter] = useState<"all" | "2D" | "3D">("all");
  
  const filteredGames = games.filter(game => {
    if (filter === "all") return true;
    return game.dimension === filter;
  });

  return (
    <div className="min-h-screen bg-background relative">
      <MatrixRain />
      <Navigation />

      {/* Hero Section */}
      <section className="relative py-20 pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 cyber-gradient opacity-20"></div>
        <div className="absolute inset-0 hex-pattern"></div>
        
        {/* ASCII Art Background */}
        <div className="absolute inset-0 flex items-center justify-center opacity-30 pointer-events-none scale-150">
          <pre className="font-terminal text-xs text-primary leading-tight">
{`
   ██████╗  █████╗ ███╗   ███╗███████╗███████╗
  ██╔════╝ ██╔══██╗████╗ ████║██╔════╝██╔════╝
  ██║  ███╗███████║██╔████╔██║█████╗  ███████╗
  ██║   ██║██╔══██║██║╚██╔╝██║██╔══╝  ╚════██║
  ╚██████╔╝██║  ██║██║ ╚═╝ ██║███████╗███████║
   ╚═════╝ ╚═╝  ╚═╝╚═╝     ╚═╝╚══════╝╚══════╝
`}
          </pre>
        </div>
        
        <FloatingParticles />
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          <div className="text-center mb-8">
            <p className="text-sm font-mono text-primary/50 mb-6 tracking-widest uppercase">GAMES</p>
            <h1 className="text-6xl md:text-7xl font-bold font-cyber mb-6 mt-32">
              <span className="text-accent">&lt;</span>
              <span className="neon-gradient bg-clip-text text-transparent">
                НАШИ_ИГРЫ
              </span>
              <span className="text-accent">/&gt;</span>
            </h1>
            <p className="font-mono text-foreground/70 text-lg mb-4">
              <span className="text-secondary">{'// '}</span>Творческие проекты, которые мы создаем
            </p>
            <p className="font-terminal text-secondary animate-pulse">
              {'> '} УНИКАЛЬНЫЕ_ИДЕИ_В_ИГРАХ {'_'}
            </p>
          </div>
        </div>
      </section>

      <section className="relative py-10">
        <FloatingParticles />
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          {/* Hero Game Showcase */}
          {games.length > 0 && (
            <div className="mb-20">
              <GameShowcase
                title={games[0].title}
                tagline={games[0].tagline}
                genre={games[0].genre}
                progress={games[0].progress}
                dimension={games[0].dimension}
              />
            </div>
          )}

          <div className="text-center mb-16">
            
            {/* Фильтры 2D/3D */}
            <div className="flex flex-wrap gap-3 justify-center items-center">
              <span className="font-mono text-sm text-primary">{'>> ФИЛЬТР:'}</span>
              <button
                onClick={() => setFilter("all")}
                className={`cyber-button px-6 py-2 font-mono text-sm transition-all ${
                  filter === "all" 
                    ? "cyber-card neon-glow-blue text-primary border-primary" 
                    : "border border-muted/40 text-muted-foreground hover:border-primary/40"
                }`}
              >
                ВСЕ
              </button>
              <button
                onClick={() => setFilter("2D")}
                className={`cyber-button px-6 py-2 font-mono text-sm transition-all ${
                  filter === "2D" 
                    ? "cyber-card neon-glow-purple text-secondary border-secondary" 
                    : "border border-muted/40 text-muted-foreground hover:border-secondary/40"
                }`}
              >
                2D ИГРЫ
              </button>
              <button
                onClick={() => setFilter("3D")}
                className={`cyber-button px-6 py-2 font-mono text-sm transition-all ${
                  filter === "3D" 
                    ? "cyber-card neon-glow-pink text-accent border-accent" 
                    : "border border-muted/40 text-muted-foreground hover:border-accent/40"
                }`}
              >
                3D ИГРЫ
              </button>
            </div>
          </div>

          <div className="space-y-12">
            {filteredGames.map((game, index) => (
              <div key={game.id} className="cyber-card p-10 space-y-8 transition-all duration-300">
                  <div className="flex items-start justify-between flex-wrap gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-3">
                      <Gamepad2 className="w-10 h-10 text-primary" />
                      <h2 className="text-2xl md:text-3xl font-bold font-cyber text-primary">
                        {game.title}
                      </h2>
                    </div>
                    <p className="font-mono text-base text-accent mb-4">{game.tagline}</p>
                  </div>
                  <span className={`px-4 py-2 text-sm font-mono border rounded-sm ${
                    game.status === "В разработке" ? "border-secondary text-secondary bg-secondary/10" :
                    game.status === "Завершена" ? "border-primary text-primary bg-primary/10" :
                    "border-muted text-muted-foreground bg-muted/10"
                  }`}>
                    {game.status}
                  </span>
                </div>

                <div className="cyber-card p-6 bg-background/50">
                  <p className="text-foreground/80 font-mono text-base leading-relaxed">
                    {game.description}
                  </p>
                </div>

                {/* Game Progress Component */}
                <GameProgress progress={game.progress} status={game.status} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="cyber-card p-6 bg-background/50">
                    <p className="font-mono text-sm text-primary mb-3 font-bold">{'>> ЖАНР'}</p>
                    <div className="flex flex-wrap gap-2">
                      {game.genre.map((g, i) => (
                        <span key={i} className="px-3 py-1 text-sm font-mono border border-primary/40 text-primary/90 bg-primary/5">
                          {g}
                        </span>
                      ))}
                    </div>
                  </div>

                <div className="cyber-card p-6 bg-background/50">
                  <p className="font-mono text-sm text-accent mb-3 font-bold">{'>> ПЛАТФОРМА'}</p>
                    <div className="flex flex-wrap gap-2">
                      {game.platforms.map((p, i) => (
                        <span key={i} className="px-3 py-1 text-sm font-mono border border-accent/40 text-accent/90 bg-accent/5">
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="cyber-card p-6 bg-background/50">
                  <p className="font-mono text-sm text-secondary mb-4 font-bold">{'>> ОСОБЕННОСТИ'}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {game.features.map((feature, i) => (
                      <div key={i} className="text-base font-mono text-foreground/80 flex items-start gap-2">
                        <span className="text-secondary mt-0.5">&gt;</span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="cyber-card p-6 bg-background/50">
                  <p className="font-mono text-sm text-primary/80 mb-4 font-bold">{'>> ТЕХНОЛОГИИ'}</p>
                  <div className="flex flex-wrap gap-2">
                    {game.tech.map((t, i) => (
                      <span key={i} className="px-3 py-1 text-xs font-mono border border-muted/40 text-muted-foreground">
                        [{t}]
                      </span>
                    ))}
                  </div>
                </div>

                {/* Ссылки на игру */}
                {game.links && (Object.keys(game.links).length > 0) && (
                  <div className="flex flex-wrap gap-3 justify-center">
                    {game.links.steam && (
                      <a
                        href={game.links.steam}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cyber-card p-4 bg-card/90 backdrop-blur-sm hover:border-primary transition-all duration-300 group flex items-center gap-3"
                        title="Steam Store"
                      >
                        <FaSteam className="w-6 h-6 text-primary transition-colors" />
                        <span className="font-mono text-sm text-foreground/90">Steam</span>
                      </a>
                    )}
                    {game.links.discord && (
                      <a
                        href={game.links.discord}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cyber-card p-4 bg-card/90 backdrop-blur-sm hover:border-secondary transition-all duration-300 group flex items-center gap-3"
                        title="Discord сервер"
                      >
                        <FaDiscord className="w-6 h-6 text-secondary transition-colors" />
                        <span className="font-mono text-sm text-foreground/90">Discord</span>
                      </a>
                    )}
                    {game.links.vk && (
                      <a
                        href={game.links.vk}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cyber-card p-4 bg-card/90 backdrop-blur-sm hover:border-accent transition-all duration-300 group flex items-center gap-3"
                        title="VK группа"
                      >
                        <FaVk className="w-6 h-6 text-accent transition-colors" />
                        <span className="font-mono text-sm text-foreground/90">VK</span>
                      </a>
                    )}
                  </div>
                )}

                <div className="flex gap-1 pt-4">
                  <div className="h-1 flex-1 bg-primary/40"></div>
                  <div className="h-1 flex-1 bg-accent/40"></div>
                  <div className="h-1 flex-1 bg-secondary/40"></div>
                </div>
              </div>
            ))}
          </div>

          {filteredGames.length === 0 && filter !== "all" && (
            <div className="cyber-card p-16 text-center">
              <Gamepad2 className="w-16 h-16 text-primary/50 mx-auto mb-6" />
              <p className="font-mono text-lg text-muted-foreground">
                <span className="text-accent">{'>> '}</span>Игры типа {filter} пока не добавлены...
              </p>
            </div>
          )}
          
          {games.length === 0 && (
            <div className="cyber-card p-16 text-center">
              <Gamepad2 className="w-16 h-16 text-primary/50 mx-auto mb-6" />
              <p className="font-mono text-lg text-muted-foreground">
                <span className="text-accent">{'>> '}</span>Скоро здесь появятся наши игры...
              </p>
            </div>
          )}
        </div>
      </section>

      <FloatingActions />
      <Footer />
    </div>
  );
};

export default Games;
